import { PropType } from 'vue';
import { mapState } from 'vuex';
import plantsClient from '@/core/services/plants';
import { Options, Vue } from 'vue-class-component';
import {
  AddressingType,
  ApartmentDTO,
  BadgeDTO,
  BuildingDTO,
  EntrancePlantDetailDTO,
  GateGuestDTO,
} from '@/../client-generator/generated-clients/plants';
import AddBadgePage from './AddBadgePage/AddBadgePage.vue';
import { SET_APP_LOADING } from '@/core/store/action-types';
import GuestSelector from './GuestSelector/GuestSelector.vue';
import BuildingSelector from './BuildingSelector/BuildingSelector.vue';
import ApartmentSelector from './ApartmentSelector/ApartmentSelector.vue';

@Options({
  name: 'DialogAddBadge',
  props: {
    plantBadges: {
      type: Object as PropType<BadgeDTO[]>,
      required: true,
    },
    isVisible: {
      type: Boolean,
      required: true,
    },
    guestBadge: {
      type: Boolean,
      required: false,
      default: false,
    },
    fromGuestTab: {
      type: Object as PropType<GateGuestDTO>,
      required: false,
      default: null,
    },
    residentBadge: {
      type: Boolean,
      required: false,
      default: false,
    },
    fromResidentTab: {
      type: Object as PropType<ApartmentDTO>,
      required: false,
      default: null,
    },
  },
  computed: {
    ...mapState(['currentEntrancePlant']),
  },
  components: {
    AddBadgePage,
    GuestSelector,
    BuildingSelector,
    ApartmentSelector,
  },
  watch: {
    isVisible: function (newValue) {
      this.isDialogVisible = newValue;
      if (!this.isDialogVisible) {
        this.step = 0;
        this.badgesList = [];
        this.selectedApartment = null;
        this.selectedBuilding = null;
        this.selectedGuest = null;
      } else {
        if (
          this.currentEntrancePlant.addressingType !==
            AddressingType.BuildingRiserFloorApartment &&
          !this.guestBadge
        ) {
          this.step = 1;
        }
      }
    },
  },
  emits: ['hide-dialog', 'update-list', 'add-badge-tab'],
})
export default class DialogAddBadge extends Vue {
  currentEntrancePlant!: EntrancePlantDetailDTO;
  plantBadges!: BadgeDTO[];
  guestBadge!: boolean;
  fromGuestTab!: GateGuestDTO;
  residentBadge!: boolean;
  fromResidentTab!: ApartmentDTO;
  badgesList: BadgeDTO[] = [];
  selectedApartment: ApartmentDTO | null = null;
  selectedBuilding: BuildingDTO | null = null;
  selectedGuest: GateGuestDTO | null = null;

  isVisible!: boolean;
  isDialogVisible = false;
  step = 0;

  mounted() {
    this.isDialogVisible = this.isVisible;
    if (this.fromGuestTab) {
      this.selectGuest(this.fromGuestTab);
    }
    if (this.fromResidentTab) {
      this.selectApartment(this.fromResidentTab);
      this.step = 2;
    }
  }

  handleHideDialog() {
    this.isDialogVisible = false;
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  get dialogTitle() {
    return this.$t('AppResourceEP.BadgesAdd');
  }

  selectApartment(apartment: ApartmentDTO) {
    this.selectedApartment = apartment;
  }

  selectBuilding(building: BuildingDTO) {
    this.selectedBuilding = building;
    this.step++;
  }

  selectGuest(guest: GateGuestDTO) {
    this.selectedGuest = guest;
    this.step = 2;
  }

  addBadge(addedBadge: BadgeDTO) {
    this.badgesList.push(addedBadge);
  }

  removeBadge(badgeToRemove: BadgeDTO) {
    this.badgesList = this.badgesList.filter(
      (badge) => badge !== badgeToRemove
    );
  }

  get secondaryButtonLabel() {
    let label: string = '';
    switch (this.step) {
      case 0:
        label = this.$t('AppResource.Cancel');
        break;
      case 1:
        if (
          this.currentEntrancePlant.addressingType ===
          AddressingType.BuildingRiserFloorApartment
        ) {
          label = this.$t('AppResource.Back');
        } else {
          label = this.$t('AppResource.Cancel');
        }
        break;
      case 2:
        if (this.fromGuestTab || this.fromResidentTab) {
          label = this.$t('AppResource.Cancel');
        } else {
          label = this.$t('AppResource.Back');
        }
        break;
    }
    return label;
  }

  handleSecondaryButtonAction() {
    switch (this.step) {
      case 0:
        this.isDialogVisible = false;
        break;

      case 1:
        if (
          this.currentEntrancePlant.addressingType !==
          AddressingType.BuildingRiserFloorApartment
        ) {
          this.isDialogVisible = false;
        } else {
          this.step--;
        }
        break;

      case 2:
        if (this.guestBadge) {
          if (this.fromGuestTab) {
            this.isDialogVisible = false;
          } else {
            this.step = 0;
          }
        } else {
          if (this.fromResidentTab) {
            this.isDialogVisible = false;
          } else {
            this.step--;
            this.selectedApartment = null;
          }
        }
        break;
    }
  }

  get buttonSubmitLabel() {
    return this.step < 2
      ? this.$t('AppResource.Continue')
      : this.$t('AppResource.Save');
  }

  get saveButtonDisabled() {
    if (this.step === 1 && this.selectedApartment === null) {
      return true;
    }
    if (this.step == 2 && this.badgesList.length === 0) {
      return true;
    }
    return false;
  }

  async handleButtonSubmitAction() {
    switch (this.step) {
      case 1:
        this.step++;
        break;

      case 2:
        if (this.fromGuestTab) { 
          this.$emit('add-badge-tab', this.badgesList[0]);
          this.isDialogVisible = false;
        } else if(this.fromResidentTab) {
          this.$emit('add-badge-tab', this.badgesList);
          this.isDialogVisible = false;
        } else {
          this.saveBadges();
        }
        break;
    }
  }

  async saveBadges() {
    if (this.currentEntrancePlant.desktopId === undefined) return;
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      await plantsClient.badgePOST(
        this.currentEntrancePlant.desktopId,
        this.badgesList
      );
      this.$toast.add({
        severity: 'success',
        summary: this.$t('AppResourceEP.BadgeAdded'),
        closable: false,
        life: 3000,
      });
    } catch (error) {
      console.log(error);
      setTimeout(() => {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          closable: false,
          life: 3000,
        });
      }, 1500);
    } finally {
      this.isDialogVisible = false;
      this.$store.dispatch(SET_APP_LOADING, false);
      this.$emit('update-list');
    }
  }
}
